import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { initializeAuth, getCurrentUser } from '../services/authService';
import { getUserPermissions } from '../features/access-management/services/accessManagementService';
import { setPermissions, setPermissionsLoading } from '../app/store/slices/authSlice';
import { selectPermissions } from '../app/store/slices/authSlice';
import { fetchCompanyInfo } from '../app/store/slices/companySlice';

// ✅ Define public routes that should NOT trigger store info API calls
const PUBLIC_ROUTES = [
  '/',
  '/home',
  '/login',
  '/register',
  '/forgot-password',
  '/privacy-policy',
  '/terms-of-service',
];

/**
 * Component to handle auth initialization based on current route
 * - Loads permissions immediately on mount if user exists (needed for route protection)
 * - Only loads store info when NOT on public pages (performance optimization)
 */
const AuthInitializer = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const permissions = useSelector(selectPermissions);
  const permissionsLoadedRef = useRef(false);
  const authInitializedRef = useRef(false);

  // ✅ Load permissions immediately on mount (critical for route protection)
  useEffect(() => {
    const loadPermissions = async () => {
      const user = getCurrentUser();
      
      // Only load if user exists and permissions aren't already loaded
      if (user && !permissionsLoadedRef.current && Object.keys(permissions || {}).length === 0) {
        permissionsLoadedRef.current = true;
        
        // ✅ Set loading state IMMEDIATELY (synchronously) before async call
        dispatch(setPermissionsLoading(true));
        
        try {
          const response = await getUserPermissions(user.id || user.email);
          const perms = response?.payload || response?.permissions || {};
          dispatch(setPermissions(perms));
        } catch (error) {
          dispatch(setPermissions({}));
        } finally {
          dispatch(setPermissionsLoading(false));
        }
      }
    };

    loadPermissions();
  }, [dispatch, permissions]); // Run when dispatch or permissions change

  // ✅ Load store info once on mount — subsequent navigations use Redux cache
  useEffect(() => {
    const user = getCurrentUser();
    if (!user) return;
    const isPublicRoute = PUBLIC_ROUTES.includes(location.pathname);
    if (isPublicRoute) return;

    if (!authInitializedRef.current) {
      authInitializedRef.current = true;
      // initializeAuth restores the user to Redux; store-info is handled below
      initializeAuth();
    }

    // ✅ dispatch goes through condition() guard — no-op if already in Redux
    dispatch(fetchCompanyInfo());
  }, []);

  return null; // This component doesn't render anything
};

export default AuthInitializer;
